import React, { FC, useState } from 'react';
import { Chip } from '@mui/material';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { downloadCSV, findErrorMessage } from 'helpers';
import { useLazyGetCommissionDownloadQuery } from 'reduxState/store/commission/api';
import { CommissionHistoryDownloadData } from 'reduxState/store/commission/types';
import './CommissionHistoryReportDownload.scss';

interface CommissionHistoryReportDownloadProps {
  downloadData: CommissionHistoryDownloadData;
  appName: string;
}

const CommissionHistoryReportDownload: FC<CommissionHistoryReportDownloadProps> = ({ downloadData, appName }) => {
  const { CreatedDate, QueryString, Status } = downloadData;
  const [csvError, setCSVError] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const [getCommissionDownloadJob] = useLazyGetCommissionDownloadQuery();

  const match = QueryString.match(/.*>"([^"]*)".*<"([^"]*)"\s*(.*)/);
  const startDate = match?.[1] || '';
  const endDate = match?.[2] || '';
  const searchQuery = match?.[3] || '';

  const downloadCSVClick = async (reportName: string) => {
    // Don't download the report again if it's in the middle of loading
    if (isLoading === true) {
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await getCommissionDownloadJob({
        appId: downloadData.ApplicationID.toString(),
        downloadId: downloadData.ID.toString(),
      });

      if (error) {
        throw new Error(findErrorMessage(error));
      }

      downloadCSV(
        data,
        `${appName.split(' ').join('_')}_Commission_History_${startDate}_to_${endDate}.csv`,
        window.document,
      );
      setLoading(false);
    } catch (error) {
      console.error(error);
      toast.error(`Failed to download CSV for ID: ${downloadData.ID}`);
      setCSVError(true);
    }
  };

  const displayDate = format(new Date(CreatedDate), 'MMM dd, yyyy');
  const reportName = `Commission History Report ${searchQuery ? `for ${searchQuery} ` : ''}(${startDate} - ${endDate})`;
  const isReady = Status.toLowerCase() === 'finalized';

  return (
    <div className="commission-report-download">
      <div className="date">{displayDate}</div>
      {!csvError && (
        <div className={isReady ? 'link' : ''} onClick={() => isReady && downloadCSVClick(reportName)}>
          {isLoading ? 'Downloading CSV. One moment please' : reportName}
          <Chip label={Status} className={`status-chip ${Status.toLowerCase()}`} />
        </div>
      )}
      {csvError && <div>Error downloading report</div>}
    </div>
  );
};

export default CommissionHistoryReportDownload;
